// @ts-strict-ignore
import { communicationService } from '@ad-engine/communication';
import { Container, Injectable } from '@wikia/dependency-injection';
import { Observable, Subject } from 'rxjs';
import { AD_ENGINE_TEMPLATE_LOADED } from '../../../communication/events/events-ad-engine';
import type { AdSlot, Dictionary, Keyof, Type } from '../../models';
import { TemplateAction } from './template-action';
import { TemplateDependenciesManager, TemplateDependency } from './template-dependencies-manager';
import { TemplateMachine } from './template-machine';
import { TemplateState } from './template-state';
import { TemplateStateHandler } from './template-state-handler';

interface TemplateMachinePayload<
	T extends Dictionary<Type<TemplateStateHandler<Keyof<T>>>[]> = any,
> {
	StateHandlerTypesDict: T;
	initialStateKey: Keyof<T>;
	emitter$: Subject<TemplateAction>;
	templateDependencies: TemplateDependency[];
}

type TemplateDependencies = (TemplateDependency | TemplateDependencies)[];

@Injectable()
export class TemplateRegistry {
	private settings = new Map<string, TemplateMachinePayload>();
	private machines = new Map<string, Set<TemplateMachine>>();

	constructor(
		private container: Container,
		private dependenciesManager: TemplateDependenciesManager,
	) {}

	has(templateName: string): boolean {
		return this.settings.has(templateName);
	}

	register<T extends Dictionary<Type<TemplateStateHandler<Keyof<T>>>[]>>(
		templateName: string,
		StateHandlerTypesDict: T,
		initialStateKey: Keyof<T>,
		templateDependencies: TemplateDependencies = [],
	): Observable<TemplateAction> {
		const emitter$ = new Subject<TemplateAction>();

		this.settings.set(templateName, {
			StateHandlerTypesDict,
			initialStateKey,
			emitter$,
			// @ts-ignore TypeScript don't understand which overload we want to retype in Parameters<Container['bind']>[0]
			templateDependencies: templateDependencies.flat<TemplateDependency>(Infinity),
		});

		return emitter$.asObservable();
	}

	async destroy(slotName: string): Promise<void> {
		const machines: TemplateMachine[] = Array.from(this.machines.get(slotName) || []);

		await Promise.all(machines.map((machine) => machine.destroy()));

		this.machines.delete(slotName);
	}

	async destroyAll(): Promise<void> {
		await Promise.all(
			Array.from(this.machines.values()).map((set) =>
				Promise.all(Array.from(set.values()).map((machine) => machine.destroy())),
			),
		);

		this.machines.clear();
	}

	init(templateName: string, templateSlot: AdSlot | null, templateParams: Dictionary = {}): void {
		if (!this.settings.has(templateName)) {
			throw new Error(`Template ${templateName} was not registered`);
		}

		communicationService.emit(AD_ENGINE_TEMPLATE_LOADED, {
			name: templateName,
			state: templateSlot ? templateSlot.getSlotName() : '',
		});

		const { StateHandlerTypesDict, initialStateKey, emitter$, templateDependencies } =
			this.settings.get(templateName);

		this.dependenciesManager.provideDependencies(
			templateName,
			templateSlot,
			templateParams,
			templateDependencies,
		);

		const templateStateMap = this.createTemplateStateMap(StateHandlerTypesDict);

		this.dependenciesManager.resetDependencies(templateDependencies);

		const machine = new TemplateMachine(templateName, templateStateMap, initialStateKey, emitter$);

		machine.init();
		this.saveMachine(templateSlot, machine);
	}

	private saveMachine(templateSlot: AdSlot | null, machine: TemplateMachine): void {
		const machinesSet =
			this.machines.get(templateSlot?.getSlotName() ?? '__default__') || new Set<TemplateMachine>();

		machinesSet.add(machine);
		this.machines.set(templateSlot?.getSlotName() ?? '__default__', machinesSet);
	}

	private createTemplateStateMap<T extends Dictionary<Type<TemplateStateHandler<Keyof<T>>>[]>>(
		StateHandlerTypesDict: T,
	): Map<Keyof<T>, TemplateState<Keyof<T>>> {
		const keyStateTuples: [Keyof<T>, TemplateState<Keyof<T>>][] = Object.keys(
			StateHandlerTypesDict,
		).map((stateKey: Keyof<T>) => [
			stateKey,
			this.createTemplateState(StateHandlerTypesDict, stateKey),
		]);

		return new Map(keyStateTuples);
	}

	private createTemplateState<T extends Dictionary<Type<TemplateStateHandler<Keyof<T>>>[]>>(
		StateHandlerTypesDict: T,
		stateKey: Keyof<T>,
	): TemplateState<Keyof<T>> {
		const StateHandlerTypes = StateHandlerTypesDict[stateKey];
		const stateHandlers = StateHandlerTypes.map((StateHandlerType) =>
			this.createStateHandler(StateHandlerType),
		);

		return new TemplateState(stateKey, stateHandlers);
	}

	private createStateHandler<T extends string>(
		StateHandlerType: Type<TemplateStateHandler<T>>,
	): TemplateStateHandler<T> {
		this.container.bind(StateHandlerType).scope('Transient');

		return this.container.get(StateHandlerType);
	}
}
